// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// One module of the API reference.
//
// The module is the unit people think in - "what is in core.maya.psd" - so it
// is the thing that gets a URL, and every symbol inside it is an anchor on
// that URL rather than a page of its own. That keeps a link to a function
// short and guessable, and keeps the browser's own find-in-page working, which
// is the first thing anybody reaches for on a reference page.
//
// Density is the whole problem here: core.maya.psd holds a hundred functions.
// The answers are a filter that narrows in place, parameters folded away
// behind a line that says what is inside them, and a jump list at the top.
// Not pagination, and not a virtualised list - both break find-in-page and
// deep links, which are the two reasons this page exists.
// ==========================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useApiModule, useApiPackage } from './api/use-api';
import { ApiClassBlock, ApiEntry } from './api/entry.component';
import { Resolver, RstBlocks } from './api/rst.component';
import { parseBlocks } from './api/rst';
import { Loading, NotPublished } from './api';

const SbFrameworkApiModuleDoc: React.FC = () => {
  const { package: packageName, module: moduleName } = useParams<{
    package: string;
    module: string;
  }>();
  const location = useLocation();
  const { data, isLoading } = useApiModule(packageName, moduleName);
  // Already in the cache from the package page or the search, so this is a
  // read rather than a fetch. It is what makes a cross-reference resolvable.
  const index = useApiPackage(packageName);

  /**
   * Turn `:func:`build_limb`` into somewhere to go, or nothing.
   *
   * Three chances, narrowing outward: this module, then a module of that name,
   * then a symbol unique in the package. A name that appears twice resolves to
   * neither - guessing which one the author meant would send people to the
   * wrong page while looking authoritative about it.
   */
  const resolve = useMemo<Resolver>(() => {
    const here = new Set<string>();
    data?.functions.forEach((entry) => here.add(entry.name));
    data?.classes.forEach((entry) => {
      here.add(entry.name);
      entry.methods.forEach((method) => here.add(`${entry.name}.${method.name}`));
    });

    const modules = new Set(index.data?.modules.map((entry) => entry.module));
    const owners = new Map<string, string[]>();
    index.data?.symbols.forEach((row) => {
      const owner = index.data?.modules[row[2]]?.module;
      if (!owner) return;
      const list = owners.get(row[0]) ?? [];
      if (!list.includes(owner)) list.push(owner);
      owners.set(row[0], list);
    });

    return (role, rawTarget) => {
      // `func()` and a leading module path are how these are often written.
      const target = rawTarget.replace(/\(\s*\)$/, '').trim();
      const short = target.split('.').pop() ?? target;

      if (here.has(target)) return `#${target}`;
      if (role !== 'mod' && here.has(short)) return `#${short}`;
      if (modules.has(target)) {
        return `/sbframework/docs/api/${packageName}/${target}`;
      }
      const found = owners.get(short);
      if (found && found.length === 1) {
        return `/sbframework/docs/api/${packageName}/${found[0]}#${short}`;
      }
      return null;
    };
  }, [data, index.data, packageName]);

  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(false);

  const needle = filter.trim().toLowerCase();
  const functions = useMemo(
    () =>
      (data?.functions ?? []).filter((entry) =>
        entry.name.toLowerCase().includes(needle)
      ),
    [data, needle]
  );
  const classes = useMemo(
    () =>
      (data?.classes ?? []).filter(
        (entry) =>
          entry.name.toLowerCase().includes(needle) ||
          entry.methods.some((method) => method.name.toLowerCase().includes(needle))
      ),
    [data, needle]
  );

  /**
   * Jump to the anchor once the module has actually arrived.
   *
   * The docs layout scrolls to a hash on its own, but it gives up after a few
   * hundred milliseconds - which is fine for a static page and never long
   * enough for one whose content is still being fetched. So this page owns the
   * scroll, keyed on the data landing.
   */
  useEffect(() => {
    if (!data || !location.hash) return;
    const id = decodeURIComponent(location.hash.slice(1));
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ block: 'start' });
  }, [data, location.hash]);

  if (isLoading) {
    return (
      <article className="font-body text-content-primary">
        <Loading />
      </article>
    );
  }

  if (!data) {
    return (
      <article className="font-body text-content-primary">
        <NotPublished what={`${moduleName} reference`} />
      </article>
    );
  }

  const total = data.functions.length + data.classes.length;

  return (
    <article className="font-body text-content-primary">
      <Link
        to={`/sbframework/docs/api/${packageName}`}
        className="text-[12.5px] text-content-muted-alt hover:text-blue no-underline transition-colors"
      >
        ← {packageName}
      </Link>

      <h2 className="mt-[8px] font-mono text-[22px] font-semibold text-content-primary tracking-[-0.3px]">
        {data.module}
      </h2>

      {data.doc.summary && (
        <div className="mt-[12px]">
          <RstBlocks
            blocks={parseBlocks(data.doc.summary)}
            resolve={resolve}
            tight
          />
        </div>
      )}

      {/* The controls for a long page, together: narrow it, or open it. */}
      <div className="mt-[22px] flex flex-wrap items-center gap-[10px]">
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder={`Filter ${total} symbol${total === 1 ? '' : 's'}`}
          aria-label={`Filter the symbols in ${data.module}`}
          className="flex-1 min-w-[200px] h-[34px] px-[12px] rounded-[8px] bg-surface-section border border-border-dark/[0.14] font-mono text-[12.5px] text-content-primary placeholder:text-content-muted-light focus:outline-none focus:border-blue/[0.5] transition-colors"
        />
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="h-[34px] px-[13px] rounded-[8px] border border-border-dark/[0.14] text-[12.5px] text-content-muted-alt hover:text-blue hover:border-blue/[0.4] transition-colors"
        >
          {expanded ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      {/* A map of the page. Bare names in a grid, because at a hundred entries
          the useful question is "is it in here" before it is "what does it
          do". Hidden while filtering, when the list below is already short. */}
      {!needle && functions.length > 6 && (
        <nav className="mt-[20px] grid grid-cols-2 sm:grid-cols-3 gap-x-[16px] gap-y-[3px]">
          {functions.map((entry) => (
            <a
              key={entry.name}
              href={`#${entry.name}`}
              className="font-mono text-[12px] text-content-muted-alt hover:text-blue no-underline truncate transition-colors"
            >
              {entry.name}
            </a>
          ))}
        </nav>
      )}

      {classes.map((entry) => (
        <ApiClassBlock
          key={entry.name}
          entry={entry}
          module={data.module}
          open={expanded}
          resolve={resolve}
        />
      ))}

      {functions.length > 0 && (
        <section className="mt-[34px]">
          {classes.length > 0 && (
            <h3 className="font-display text-[15px] font-bold text-content-primary mb-[4px]">
              Functions
            </h3>
          )}
          {functions.map((entry) => (
            <ApiEntry
              key={entry.name}
              entry={entry}
              module={data.module}
              open={expanded}
              resolve={resolve}
            />
          ))}
        </section>
      )}

      {needle && functions.length === 0 && classes.length === 0 && (
        <p className="mt-[22px] text-[13.5px] text-content-muted-alt">
          Nothing in {data.module} matches “{filter.trim()}”.
        </p>
      )}
    </article>
  );
};

export default SbFrameworkApiModuleDoc;
